/* eslint-disable react/jsx-key */
import { Path } from '@vimeo/iris/components';
import { Header, Link, Paragraph } from '@vimeo/iris/typography';

import CodeBlock from 'src/_components/CodeBlock';
import { FancyList, FancyListItem, FancyListTableOfContents } from 'src/_components/FancyList';
import GuideTable from 'src/_components/GuideTable';
import GuideWrapper from 'src/_components/GuideWrapper';
import Meta from 'src/_components/Meta';
import StyledNotice from 'src/_components/StyledNotice';

export default function UploadVideos() {
  return (
    <>
      <Meta
        title="Uploading Videos with the Vimeo API"
        description="Learn how to upload videos with the Vimeo API, edit video metadata, and more. Beginners welcome!"
      />
      <GuideWrapper
        breadcrumbs={
          <Path>
            <Path.Link href={'/'}>Home</Path.Link>
            <Path.Link href={'/api/guides/start'}>API</Path.Link>
            <Path.Current>Working with Video Uploads</Path.Current>
          </Path>
        }
        label="Working with Video Uploads"
        nav={{ page: 'upload-videos' }}
      >
        <Header>Working with Video Uploads</Header>

        <Paragraph size="2">
          The Vimeo API includes a full set of features for uploading and managing video files. With
          this functionality, you can access all the amazing upload capabilities of{' '}
          <Link variant="minimal" href="https://vimeo.com">
            vimeo.com
          </Link>{' '}
          through your own front end in your own application.
        </Paragraph>

        <Paragraph size="2">
          Here are step-by-step instructions for uploading videos through the API and managing
          related tasks.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> This information is current for our latest API release, version
          3.4. For complete details on 3.4, see our{' '}
          <Link variant="minimal" href="/api/changelog">
            Changelog
          </Link>
          .
        </StyledNotice>

        <Header size="2" id="before-you-begin">
          Before you begin
        </Header>
        <Paragraph size="2">To use this guide, you need:</Paragraph>

        <ul>
          <li>
            <strong>A video.</strong> This is what you'll be uploading. The maximum file size is 300
            GB, and the maximum duration is 24 hours.
          </li>
          <li>
            <strong>A Vimeo account.</strong> This is where you'll be uploading it.
          </li>
          <li>
            <strong>An API application.</strong> If you want to know why, see{' '}
            <Link variant="minimal" href="/api/guides/start">
              Getting Started: The Basics
            </Link>
            .
          </li>
          <li>
            <strong>An approved developer application if you’re a Vimeo Free subscriber. </strong>{' '}
            Submit a{' '}
            <Link variant="minimal" href="https://vimeo.com/help/contact">
              support ticket
            </Link>{' '}
            requesting Vimeo API upload access.
          </li>
          <li>
            <strong>Upload access for the API application.</strong> You need to request this. See{' '}
            <Link variant="minimal" href="/api/guides/videos/upload#enable-upload-access">
              Getting Started: Uploading Videos
            </Link>{' '}
            for details.
          </li>
          <li>
            <strong>
              An access token with the{' '}
              <code>
                <b>upload</b>
              </code>{' '}
              and{' '}
              <code>
                <b>edit</b>
              </code>{' '}
              scopes.
            </strong>{' '}
            See{' '}
            <Link variant="minimal" href="/api/guides/start">
              Getting Started: The Basics
            </Link>{' '}
            to learn more.
          </li>
        </ul>

        <StyledNotice>
          <strong>NOTE:</strong> You might also consider using our official libraries for PHP,
          Python, Node.js, and a dozen other programming languages. The libraries help to simplify
          complicated coding by handling all the HTTP requests on your behalf. For more information,
          see our{' '}
          <Link variant="minimal" href="/api/libraries">
            Libraries and SDKs
          </Link>{' '}
          page.
        </StyledNotice>

        <Header size="2" id="understanding-upload-approaches">
          Understanding upload approaches
        </Header>
        <Paragraph size="2">There are three main approaches for uploading video:</Paragraph>

        <ul>
          <li>
            Resumable, or tus, after the open-source{' '}
            <Link variant="minimal" href="https://tus.io/">
              tus protocol
            </Link>
          </li>
          <li>Form-based, also known as POST</li>
          <li>Pull</li>
        </ul>

        <Paragraph size="2">
          In the following sections, we describe these approaches and help you determine which one
          to use. But before we get too carried away, we want to stress that each approach begins
          with a POST request, which adds a video placeholder to your account. This placeholder
          continues to exist, even if you don't subsequently upload a video file to it. To prevent
          your account from getting bogged down by unused placeholders, you should start your chosen
          upload procedure only when you're ready to upload video.
        </Paragraph>

        <Header size="3" id="about-resumable-uploads">
          About resumable uploads
        </Header>
        <Paragraph size="2">
          With the resumable or tus approach, you can poll the progress of the upload as it's
          occurring, and pick up a partial upload where it left off — whether that's because of a
          lost internet connection or because you've programmed pause functionality into your app.
        </Paragraph>

        <Paragraph size="2">
          Since you have access to the current state of the upload, you can utilize progress bars or
          percent-complete counters to enhance user experience, which you can't do with the other
          approaches. And just being able to resume an upload is the definition of user-friendly
          design, so we recommend this approach for just about everyone. But these kinds of features
          take time to implement. If you're looking to upload with minimum effort, you might want to
          consider an alternative.
        </Paragraph>

        <Header size="3" id="about-form-based-uploads">
          About form-based uploads
        </Header>
        <Paragraph size="2">
          With the form-based or POST approach, your end user uploads the video to Vimeo through an
          HTML form, which you deploy in your app or through a link.
        </Paragraph>

        <Paragraph size="2">
          Integrating this approach is easy enough. Your code doesn't manage the upload itself, and
          you don't even need to create your own form. But the video comes to us as POST data, so
          the upload isn't resumable. Also, if the upload fails, your end user needs to resend the
          entire video file.
        </Paragraph>

        <Header size="3" id="about-pull-uploads">
          About pull uploads
        </Header>
        <Paragraph size="2">
          With the pull approach, you point us toward a video file that already exists on the
          internet. We make a copy of the video, assign it a place on Vimeo, and otherwise treat it
          like any other upload. We even handle any connectivity issues that might come up. All we
          need is the absolute path to the video file.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> If the video resides on a content delivery network, make sure that
          the URL is unsigned and public, or possibly presigned if security is a concern. See{' '}
          <Link variant="minimal" href="#pull-approach">
            Using the pull approach
          </Link>{' '}
          below for more information.
        </StyledNotice>

        <Paragraph size="2">
          As long as you have this information, the pull approach is the easiest one to integrate.
          But the key is having this information, which means that someone has already uploaded the
          video and that it's readily available for us to find. If this approach makes sense for
          your app, then use it, but most apps are better with tus or POST.
        </Paragraph>

        <Header size="2" id="resumable-approach">
          Using the resumable approach
        </Header>
        <Paragraph size="2">Here's the general procedure for a resumable upload:</Paragraph>

        <FancyListTableOfContents
          anchorPrefix="resumable-approach"
          items={['Create the video.', 'Upload the video file.', 'Verify the upload.']}
        />

        <Paragraph size="2">
          The following sections describe these steps in scintillating detail.
        </Paragraph>

        <FancyList anchorPrefix="resumable-approach">
          <FancyListItem>
            <Header size="3">Create the video</Header>
            <Paragraph size="2">
              Make an authenticated POST request to <code>/me/videos</code>:
            </Paragraph>

            <CodeBlock endpoint={{ method: 'POST', resource: '/me/videos' }} />

            <Paragraph size="2">
              See{' '}
              <Link variant="minimal" href="#table-1">
                Table 1
              </Link>{' '}
              for the headers to include. Remember, it's better not to send this request until you
              have a video file ready to upload.
            </Paragraph>

            <GuideTable
              tableNumber={1}
              description={
                <span>
                  Headers for POST to <code>/me/videos</code>
                </span>
              }
              headers={['Header', 'Set value to']}
              rows={[
                [
                  <code>
                    <b>Authorization</b>
                  </code>,
                  <code>bearer {`{access_token}`}</code>,
                ],
                [
                  <code>
                    <b>Content-Type</b>
                  </code>,
                  <code>application/json</code>,
                ],
                [
                  <code>
                    <b>Accept</b>
                  </code>,
                  <code>application/vnd.vimeo.*+json;version=3.4</code>,
                ],
              ]}
            />

            <StyledNotice>
              <strong>NOTE:</strong> In most cases, you should include the{' '}
              <code>
                <b>Accept</b>
              </code>{' '}
              header with your request and set it to the given value; for more information, see{' '}
              <Link variant="minimal" href="/api/common-formats#using-the-accept-header">
                Using Common Formats and Parameters
              </Link>
              . The resumable approach presents an exception to this, which we'll cover briefly in a
              bit.
            </StyledNotice>

            <Paragraph size="2">
              In the body of the request, set the{' '}
              <code>
                <b>upload.approach</b>
              </code>{' '}
              parameter to the value <code>tus</code> and the{' '}
              <code>
                <b>upload.size</b>
              </code>{' '}
              parameter to the length of the video file in bytes:
            </Paragraph>

            <CodeBlock language="json">
              {{
                upload: {
                  approach: 'tus',
                  size: '{size}',
                },
              }}
            </CodeBlock>

            <StyledNotice>
              <strong>NOTE:</strong> The{' '}
              <code>
                <b>upload.size</b>
              </code>{' '}
              field is required for the resumable approach, so be sure not to omit it here.
            </StyledNotice>

            <Paragraph size="2">
              The response returns the HTTP 200 status code and a number of fields about the new
              video, including{' '}
              <code>
                <b>upload.upload_link</b>
              </code>
              , which gives you the web address for uploading the video file, and{' '}
              <code>
                <b>uri</b>
              </code>
              , which gives you the eventual location of the video on Vimeo. Make sure to check the
              response for the{' '}
              <code>
                <b>upload.approach</b>
              </code>{' '}
              field; the value here should be <code>tus</code>. If it isn't, try this step again,
              and take extra care that you're sending a POST request and that you specify{' '}
              <code>tus</code> as the approach in the request body.
            </Paragraph>
          </FancyListItem>
          <FancyListItem>
            <Header size="3">Upload the video file</Header>
            <Paragraph size="2">
              PATCH the binary data of the entire video file to the URL from{' '}
              <code>
                <b>upload.upload_link</b>
              </code>
              , along with some custom tus headers:
            </Paragraph>

            <CodeBlock
              endpoint={{
                method: 'PATCH',
                resource: '{upload.upload_link}',
                httpPrefix: false,
              }}
            />

            <Paragraph size="2">
              The headers appear in{' '}
              <Link variant="minimal" href="#table-2">
                Table 2
              </Link>
              .
            </Paragraph>

            <GuideTable
              tableNumber={2}
              description={
                <span>
                  Headers for PATCH to <code>{`{upload.upload_link}`}</code>
                </span>
              }
              headers={['Header', 'Set value to']}
              rows={[
                [
                  <code>
                    <b>Tus-Resumable</b>
                  </code>,
                  <code>1.0.0</code>,
                ],
                [
                  <code>
                    <b>Upload-Offset</b>
                  </code>,
                  <span>
                    <code>0</code> for the initial request; the stated upload offset for subsequent
                    requests
                  </span>,
                ],
                [
                  <code>
                    <b>Content-Type</b>
                  </code>,
                  <code>application/offset+octet-stream</code>,
                ],
              ]}
            />

            <Paragraph size="2">
              Remember that exception about using the{' '}
              <code>
                <b>Accept</b>
              </code>{' '}
              header? Since{' '}
              <code>
                <b>Accept</b>
              </code>{' '}
              is ignored during the resumable upload process, it's not needed in this instance.
            </Paragraph>

            <Paragraph size="2">Use byte 0 as the start of the binary video data.</Paragraph>

            <StyledNotice>
              <strong>NOTE:</strong> This high-end pro tip might not apply to general-purpose
              resumable uploads. But if you're using a tus library that breaks the video file into
              separate chunks based on a maximum size value, we recommend setting the chunk size in
              the range of 128–512 MB. Smaller chunk sizes might result in slower upload speeds,
              especially when there are many chunks.
            </StyledNotice>

            <Paragraph size="2">
              In the response, look for the{' '}
              <code>
                <b>Upload-Offset</b>
              </code>{' '}
              header, and compare it with the size of the video file:
            </Paragraph>

            <ul>
              <li>
                <strong>
                  If the value of{' '}
                  <code>
                    <b>Upload-Offset</b>
                  </code>{' '}
                  is the same as the size of the video file,
                </strong>{' '}
                we received the entire file. Consider this video uploaded.
              </li>
              <li>
                <strong>
                  If the value of{' '}
                  <code>
                    <b>Upload-Offset</b>
                  </code>{' '}
                  is less than the size of the video file,
                </strong>{' '}
                we didn't receive the entire file, but you can easily resume the transfer.
              </li>
            </ul>

            <Paragraph size="2">
              To resume, send another PATCH request to{' '}
              <code>
                <b>upload.upload_link</b>
              </code>
              , only this time, set the{' '}
              <code>
                <b>Upload-Offset</b>
              </code>{' '}
              header to the value of{' '}
              <code>
                <b>Upload-Offset</b>
              </code>{' '}
              from the previous response, and include only the unsent portion of the binary data
              (which starts at the byte equal to{' '}
              <code>
                <b>Upload-Offset</b>
              </code>
              ). The API responds with a new{' '}
              <code>
                <b>Upload-Offset</b>
              </code>{' '}
              header for you to compare against the size of the video file. If we still haven't
              received all the data from the file, resume the transfer again from this new offset.
            </Paragraph>

            <StyledNotice>
              <strong>NOTE:</strong> No matter how many times you PATCH to{' '}
              <code>
                <b>upload.upload_link</b>
              </code>
              , always be sure to supply the most recent{' '}
              <code>
                <b>Upload-Offset</b>
              </code>{' '}
              value in the header. If you don't, the API comes back with the HTTP 409 conflict
              status code. Check{' '}
              <code>
                <b>Upload-Offset</b>
              </code>
              , and try again.
            </StyledNotice>
          </FancyListItem>
          <FancyListItem>
            <Header size="3">Verify the upload</Header>
            <Paragraph size="2">
              To monitor the progress of the upload, send a HEAD request to{' '}
              <code>
                <b>upload.upload_link</b>
              </code>
              :
            </Paragraph>

            <CodeBlock
              endpoint={{
                method: 'HEAD',
                resource: '{upload.upload_link}',
                httpPrefix: false,
              }}
            />

            <Paragraph size="2">
              See{' '}
              <Link variant="minimal" href="#table-3">
                Table 3
              </Link>{' '}
              for the headers.
            </Paragraph>

            <GuideTable
              tableNumber={3}
              description={
                <span>
                  Headers for HEAD to <code>{`{upload.upload_link}`}</code>
                </span>
              }
              headers={['Header', 'Set value to']}
              rows={[
                [
                  <code>
                    <b>Tus-Resumable</b>
                  </code>,
                  <code>1.0.0</code>,
                ],
                [
                  <code>
                    <b>Accept</b>
                  </code>,
                  <code>application/vnd.vimeo.*+json;version=3.4</code>,
                ],
              ]}
            />

            <Paragraph size="2">
              The response returns the HTTP 200 status code and the{' '}
              <code>
                <b>Upload-Length</b>
              </code>{' '}
              and{' '}
              <code>
                <b>Upload-Offset</b>
              </code>{' '}
              headers, among others. Determine the completeness of the upload by comparing the
              values of{' '}
              <code>
                <b>Upload-Length</b>
              </code>{' '}
              and{' '}
              <code>
                <b>Upload-Offset</b>
              </code>
              :
            </Paragraph>

            <ul>
              <li>
                <strong>
                  If{' '}
                  <code>
                    <b>Upload-Length</b>
                  </code>{' '}
                  and{' '}
                  <code>
                    <b>Upload-Offset</b>
                  </code>{' '}
                  are equal,
                </strong>{' '}
                we've received the entire video file.
              </li>
              <li>
                <strong>
                  If{' '}
                  <code>
                    <b>Upload-Length</b>
                  </code>{' '}
                  is larger than{' '}
                  <code>
                    <b>Upload-Offset</b>
                  </code>
                  ,
                </strong>{' '}
                we haven't.
              </li>
            </ul>

            <Paragraph size="2">
              To complete the upload, PATCH the remainder of the binary video data to{' '}
              <code>
                <b>upload.upload_link</b>
              </code>{' '}
              starting from{' '}
              <code>
                <b>Upload-Offset</b>
              </code>
              :
            </Paragraph>

            <CodeBlock
              endpoint={{
                method: 'PATCH',
                resource: '{upload.upload_link}',
                httpPrefix: false,
              }}
            />

            <Paragraph size="2">
              Set the{' '}
              <code>
                <b>Tus-Resumable</b>
              </code>{' '}
              header to <code>1.0.0</code>,{' '}
              <code>
                <b>Upload-Offset</b>
              </code>{' '}
              to the upload offset value,{' '}
              <code>
                <b>Content-Type</b>
              </code>{' '}
              to <code>application/offset+octet-stream</code>, and{' '}
              <code>
                <b>Accept</b>
              </code>{' '}
              to <code>application/vnd.vimeo.*+json;version=3.4</code>, like in{' '}
              <Link variant="minimal" href="#table-2">
                Table 2
              </Link>{' '}
              above.
            </Paragraph>
          </FancyListItem>
        </FancyList>

        <Header size="2" id="form-approach">
          Using the form-based approach
        </Header>
        <Paragraph size="2">
          The form-based approach utilizes an HTML form to upload the video file. Here's the
          procedure:
        </Paragraph>

        <FancyListTableOfContents
          anchorPrefix="form-based-approach"
          items={['Create the video.', 'Deploy the HTML form.']}
        />

        <Paragraph size="2">We look at each step in the following sections.</Paragraph>

        <FancyList anchorPrefix="form-based-approach">
          <FancyListItem>
            <Header size="3">Create the video</Header>
            <Paragraph size="2">
              Is your video file ready to upload? Great! Begin with an authenticated POST request to{' '}
              <code>/me/videos</code>:
            </Paragraph>

            <CodeBlock
              endpoint={{
                method: 'POST',
                resource: '/me/videos',
              }}
            />

            <Paragraph size="2">
              Set the{' '}
              <code>
                <b>Authorization</b>
              </code>{' '}
              header to <code>bearer {`{access_token}`}</code>,{' '}
              <code>
                <b>Content-Type</b>
              </code>{' '}
              to <code>application/json</code>, and{' '}
              <code>
                <b>Accept</b>
              </code>{' '}
              to <code>application/vnd.vimeo.*+json;version=3.4</code>, like in{' '}
              <Link variant="minimal" href="#table-1">
                Table 1
              </Link>{' '}
              above.
            </Paragraph>

            <Paragraph size="2">
              In the body of the request, set the{' '}
              <code>
                <b>upload.approach</b>
              </code>{' '}
              parameter to <code>post</code> and set the{' '}
              <code>
                <b>upload.redirect_url</b>
              </code>{' '}
              parameter to the web address of your completion page:
            </Paragraph>

            <CodeBlock language="json">
              {{
                upload: {
                  approach: 'post',
                  redirect_url: '{url}',
                },
              }}
            </CodeBlock>

            <Paragraph size="2">
              Your redirect URL can also include query parameters, as in{' '}
              <code>https://www.example.com?param=value</code>.
            </Paragraph>

            <Paragraph size="2">
              The{' '}
              <code>
                <b>upload.form</b>
              </code>{' '}
              field in the response contains the HTML that you need for the next step. But before
              you zoom ahead, take a moment to verify that{' '}
              <code>
                <b>upload.approach</b>
              </code>{' '}
              has the value <code>post</code>. Any other value means something went wrong, so
              double-check your HTTP method and body payload.
            </Paragraph>
          </FancyListItem>
          <FancyListItem>
            <Header size="3">Deploy the HTML form</Header>
            <Paragraph size="2">
              Take the HTML markup from the{' '}
              <code>
                <b>upload.form</b>
              </code>{' '}
              field, which you received in the previous step, and use it to create the upload form
              for the video.
            </Paragraph>

            <Paragraph size="2">
              The{' '}
              <code>
                <b>upload.form</b>
              </code>{' '}
              field gives you perfectly adequate HTML, so you can place it in the body element of an
              HTML document for a functional (though perhaps a tad uninteresting) user interface.
              More likely, you'll want to extract the workings from our HTML form and put them in a
              form of your own:
            </Paragraph>

            <ul>
              <li>
                For the <code>action</code> attribute, use the value of the{' '}
                <code>
                  <b>upload.upload_link</b>
                </code>{' '}
                field from the API response in Step 1.
              </li>
              <li>
                Be sure to include an <code>{`<input type="file">`}</code> element with the name{' '}
                <code>file_data</code>.
              </li>
            </ul>

            <Paragraph size="2">
              After you deploy the HTML form, it's ready to receive the upload. Direct your end user
              to the form's location. Upon submission, the browser goes to the redirect URL, which
              we append with the{' '}
              <code>
                <b>video_uri</b>
              </code>{' '}
              parameter, like this:
            </Paragraph>

            <CodeBlock language="bash">
              {`{url}`}?video_uri=/videos/{`{video_id}`}
            </CodeBlock>

            <Paragraph size="2">
              This parameter contains the endpoint of the uploaded video.
            </Paragraph>

            <StyledNotice>
              <strong>NOTE:</strong> Be sure to use the upload form one time only. To upload a new
              video, you must make a new POST request to <code>/me/videos</code>.
            </StyledNotice>
          </FancyListItem>
        </FancyList>

        <Header size="2" id="pull-approach">
          Using the pull approach
        </Header>
        <Paragraph size="2">
          To pull a video file from somewhere else on the internet, send an authenticated POST
          request to <code>/me/videos</code>:
        </Paragraph>

        <CodeBlock endpoint={{ method: 'POST', resource: '/me/videos' }} />

        <Paragraph size="2">
          Check{' '}
          <Link variant="minimal" href="#table-1">
            Table 1
          </Link>{' '}
          above for the headers. You want to send{' '}
          <code>
            <b>Authorization</b>
          </code>{' '}
          as <code>bearer {`{access_token}`}</code>,{' '}
          <code>
            <b>Content-Type</b>
          </code>{' '}
          as <code>application/json</code>, and{' '}
          <code>
            <b>Accept</b>
          </code>{' '}
          as <code>application/vnd.vimeo.*+json;version=3.4</code>.
        </Paragraph>

        <Paragraph size="2">
          Use the body of the request to set the{' '}
          <code>
            <b>upload.approach</b>
          </code>{' '}
          parameter to <code>pull</code>, the{' '}
          <code>
            <b>upload.size</b>
          </code>{' '}
          parameter to the length of the video file in bytes, and the{' '}
          <code>
            <b>upload.link</b>
          </code>{' '}
          parameter to the full URL of the video file:
        </Paragraph>

        <CodeBlock language="json">
          {{
            upload: {
              approach: 'pull',
              size: '{size}',
              link: '{url}',
            },
          }}
        </CodeBlock>

        <Paragraph size="2">
          A link to a web page with an embedded video isn't enough information for our upload
          server, unfortunately. The URL must resolve to the video file directly. Be sure to encode
          any unsafe characters and avoid reserved characters as you normally would, only don't
          escape the slashes in the path. Also, as we've mentioned before, if you're pulling a video
          from a content delivery network, the video's URL should be unsigned and public to prevent
          the URL from expiring on us before we have a chance to finish the upload. A presigned URL
          should be alright if it expires at least six hours after the time that we receive it.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> What about redirects? We can handle those, too, as long as the
          redirect points to a video on the same domain or host. If the resource is elsewhere, the
          pull won't work.
        </StyledNotice>

        <Paragraph size="2">
          See{' '}
          <Link variant="minimal" href="#table-4">
            Table 4
          </Link>{' '}
          for a summary of requirements.
        </Paragraph>

        <GuideTable
          tableNumber={4}
          description={<span>URL requirements for a pull upload</span>}
          headers={['Requirement']}
          rows={[
            ['The link must resolve directly to a video file.'],
            ['The link must be URL-encoded.'],
            [
              'If the video is on a content delivery network, the link must be unsigned and public.',
            ],
            ['The link can be presigned if it expires in six hours or more.'],
            ['The link can be a redirect if it points to a video on the same domain or host.'],
            ['The link must be no longer than 16,384 characters.'],
          ]}
        />

        <Paragraph size="2">
          The response comes back with the HTTP 201 status code and complete information about our
          copy of the video. It also contains the{' '}
          <code>
            <b>upload.approach</b>
          </code>{' '}
          field, the value of which should be <code>pull</code>. Check that it is, and if it's not,
          go over your work and try this step again.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> Since our uploader accepts a wide range of video codecs and file
          types, we can't analyze the file until it's on our server. Therefore, links to invalid or
          non-video files (like MP3 or PDF) still return HTTP 201 and generate a video URI. However,
          in these cases, if you send a GET request to the URI, the{' '}
          <code>
            <b>status</b>
          </code>{' '}
          field in the response may contain either <code>uploading_error</code> or{' '}
          <code>transcoding_error</code>.
        </StyledNotice>

        <Header size="2" id="setting-video-metadata">
          Setting video metadata
        </Header>
        <Paragraph size="2">
          Video uploads on Vimeo include metadata such as the name of the video and the video's
          privacy settings. Besides being useful as titles and text descriptors, metadata are also a
          key component in strategies for{' '}
          <Link
            variant="minimal"
            href="https://vimeo.com/blog/post/video-seo-how-structured-data-and-rich-snippets"
          >
            search engine optimization
          </Link>
          .
        </Paragraph>

        <Paragraph size="2">
          You can specify values for a video's metadata fields in the body of the initial POST
          request of an upload, like this:
        </Paragraph>

        <CodeBlock language="json">
          {{
            upload: {
              approach: 'tus',
              size: 800000000,
            },
            name: 'My Video',
            privacy: {
              view: 'nobody',
            },
          }}
        </CodeBlock>

        <Paragraph size="2">
          <Link variant="minimal" href="#table-5">
            Table 5
          </Link>{' '}
          presents some common video metadata fields, but there are many others. For a complete
          list, see the{' '}
          <Link variant="minimal" href="/api/reference/videos#edit_video">
            API Reference
          </Link>
          .
        </Paragraph>

        <GuideTable
          tableNumber={5}
          description="Common video metadata fields"
          headers={['Metadata field', 'Determines', 'Possible values']}
          rows={[
            [
              <code>
                <b>name</b>
              </code>,
              'The title of the video.',
              'Any*',
            ],
            [
              <code>
                <b>description</b>
              </code>,
              'A brief description of the video.',
              'Any',
            ],
            [
              <code>
                <b>privacy.download</b>
              </code>,
              'Whether people can download the video.',
              <span>
                <code>true</code>, <code>false</code>
              </span>,
            ],
            [
              <code>
                <b>privacy.embed</b>
              </code>,
              'Where the video can be embedded.',
              <span>
                <code>private</code>, <code>public</code>, <code>whitelist</code>†
              </span>,
            ],
            [
              <code>
                <b>privacy.view</b>
              </code>,
              'Who can view the video on Vimeo.',
              <span>
                <code>anybody</code>, <code>contacts</code>, <code>disable</code>,{' '}
                <code>nobody</code>, <code>password</code>, <code>unlisted</code>,{' '}
                <code>users</code>†
              </span>,
            ],
          ]}
        />

        <Paragraph size="3">
          * If you don't specify a name for your video, we automatically give it the name{' '}
          <em>Untitled</em>, unless you upload it with the pull approach. In that case, we use the
          file name of the video.
        </Paragraph>

        <Paragraph size="3">
          † Some of these values require additional parameters. See the{' '}
          <Link variant="minimal" href="/api/reference/videos#edit_video">
            API Reference
          </Link>{' '}
          for more information.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> To set metadata in videos that you've already uploaded, PATCH the
          fields and values to <code>https://api.vimeo.com/videos/{'{video_id}'}</code>. For more
          information, see the{' '}
          <Link variant="minimal" href="/api/reference/videos#edit_video">
            API Reference
          </Link>
          .
        </StyledNotice>

        <Header size="2" id="replacing-a-source-file">
          Replacing the source file of a video
        </Header>
        <Paragraph size="2">
          Instead of uploading to an entirely new video, you can replace the source file of an
          existing video.
        </Paragraph>

        <StyledNotice>
          <strong>NOTE:</strong> Please keep in mind that the updated video retains all its
          settings, including its URL and thumbnail image. To change the video's settings, see{' '}
          <Link variant="minimal" href="/api/reference/videos#edit_video">
            Edit a video
          </Link>{' '}
          in the API Reference. To change the video's thumbnail, see{' '}
          <Link variant="minimal" href="/api/reference/videos#edit_video_thumbnail">
            Edit a video thumbnail
          </Link>
          . You can't change the video's URL, so if you want a new one, you'll need to create a new
          video.
        </StyledNotice>

        <Paragraph size="2">
          The process for replacing the video source depends on the upload method that you use to
          send us the new file.
        </Paragraph>

        <Header size="3" id="replacing-source-file-resumable-upload">
          Replacing the source file through a resumable upload
        </Header>
        <Paragraph size="2">
          Send a POST request to <code>/videos/{`{video_id}`}/versions</code>, where{' '}
          <code>video_id</code> is the unique identifier code of the video that you would like to
          replace:
        </Paragraph>

        <CodeBlock
          endpoint={{
            method: 'POST',
            resource: '/videos/{video_id}/versions',
          }}
        />

        <Paragraph size="2">
          <Link variant="minimal" href="#table-6">
            Table 6
          </Link>{' '}
          shows the necessary headers.
        </Paragraph>

        <GuideTable
          tableNumber={6}
          description={
            <span>
              Headers for POST to <code>/videos/{`{video_id}`}/versions</code>
            </span>
          }
          headers={['Header', 'Set value to']}
          rows={[
            [
              <code>
                <b>Content-Type</b>
              </code>,
              <code>application/json</code>,
            ],
            [
              <code>
                <b>Accept</b>
              </code>,
              <code>application/vnd.vimeo.*+json;version=3.4</code>,
            ],
          ]}
        />

        <Paragraph size="2">
          In the body of the request, set{' '}
          <code>
            <b>upload.status</b>
          </code>{' '}
          to <code>in_progress</code>,{' '}
          <code>
            <b>upload.size</b>
          </code>{' '}
          to the size of the new source file in bytes, and{' '}
          <code>
            <b>upload.approach</b>
          </code>{' '}
          to <code>tus</code>:
        </Paragraph>

        <CodeBlock language="json">
          {{
            file_name: '{file name}',
            upload: {
              status: 'in_progress',
              size: '{size}',
              approach: 'tus',
            },
          }}
        </CodeBlock>

        <Paragraph size="2">
          The response contains the{' '}
          <code>
            <b>upload.upload_link</b>
          </code>{' '}
          field, which gives you the upload URL for the new source file. Take this URL to Step 2 of
          the resumable upload approach, and continue the upload from there.
        </Paragraph>

        <Header size="3" id="replacing-source-file-form-upload">
          Replacing the source file through a form-based upload
        </Header>
        <Paragraph size="2">
          Send a POST request to <code>/videos/{`{video_id}`}/versions</code>, where{' '}
          <code>video_id</code> is the unique identifier code of the video that you would like to
          replace:
        </Paragraph>

        <CodeBlock
          endpoint={{
            method: 'POST',
            resource: '/videos/{video_id}/versions',
          }}
        />

        <Paragraph size="2">
          See{' '}
          <Link variant="minimal" href="#table-6">
            Table 6
          </Link>{' '}
          above for the header values. Set{' '}
          <code>
            <b>Authorization</b>
          </code>{' '}
          to <code>bearer {`{access_token}`}</code> and{' '}
          <code>
            <b>Accept</b>
          </code>{' '}
          to <code>application/vnd.vimeo.*+json;version=3.4</code>.
        </Paragraph>

        <Paragraph size="2">
          In the body of the request, set{' '}
          <code>
            <b>upload.status</b>
          </code>{' '}
          to <code>in_progress</code> and{' '}
          <code>
            <b>upload.approach</b>
          </code>{' '}
          to <code>post</code>:
        </Paragraph>

        <CodeBlock language="json">
          {{
            file_name: '{file name}',
            upload: {
              status: 'in_progress',
              approach: 'post',
            },
          }}
        </CodeBlock>

        <Paragraph size="2">
          The response contains the{' '}
          <code>
            <b>upload.form</b>
          </code>{' '}
          field, which gives you the HTML markup for the upload form. To continue the upload, use
          this markup in Step 2 of the form-based approach.
        </Paragraph>

        <Header size="3" id="replacing-source-file-pull-upload">
          Replacing the source file through a pull upload
        </Header>
        <Paragraph size="2">
          Send a POST request to <code>/videos/{`{video_id}`}/versions</code>, where{' '}
          <code>video_id</code> is the unique identifier code of the video that you would like to
          replace:
        </Paragraph>

        <CodeBlock
          endpoint={{
            method: 'POST',
            resource: '/videos/{video_id}/versions',
          }}
        />

        <Paragraph size="2">
          Send the{' '}
          <code>
            <b>Authorization</b>
          </code>{' '}
          header as <code>bearer {`{access_token}`}</code> and{' '}
          <code>
            <b>Accept</b>
          </code>{' '}
          as <code>application/vnd.vimeo.*+json;version=3.4</code>, like in{' '}
          <Link variant="minimal" href="#table-6">
            Table 6
          </Link>{' '}
          above.
        </Paragraph>

        <Paragraph size="2">
          In the body of the request, set{' '}
          <code>
            <b>upload.status</b>
          </code>{' '}
          to <code>in_progress</code>,{' '}
          <code>
            <b>upload.size</b>
          </code>{' '}
          to the size of the new source file in bytes,{' '}
          <code>
            <b>upload.approach</b>
          </code>{' '}
          to <code>pull</code>, and{' '}
          <code>
            <b>upload.link</b>
          </code>{' '}
          to the complete URL of the replacement video file:
        </Paragraph>

        <CodeBlock language="json">
          {{
            file_name: '{file name}',
            upload: {
              status: 'in_progress',
              size: '{size}',
              approach: 'pull',
              link: '{url}',
            },
          }}
        </CodeBlock>

        <Paragraph size="2">This is the only step. We promise.</Paragraph>

        <Header size="2" id="handling-errors">
          Handling errors
        </Header>
        <Paragraph size="2">
          Something didn't go as expected? We're here to right the ship. The following sections give
          explanations and advice for fixing the most common upload errors.
        </Paragraph>

        <Paragraph size="2">
          If you're receiving a different error, our{' '}
          <Link variant="minimal" href="/api/reference/videos#upload_video">
            API Reference
          </Link>{' '}
          is at your disposal.
        </Paragraph>

        <Header size="3" id="error-code-2204" data-no-sidebar>
          Error code 2204: parameter problem
        </Header>

        <Paragraph size="2">
          When the API returns error code 2204 with HTTP status 400 Bad Request, you're using at
          least one unrecognized parameter. The{' '}
          <code>
            <b>invalid_parameters</b>
          </code>{' '}
          block of the response gives a separate report and error code for each problematic field,
          which helps you to pinpoint exactly what's going on.
        </Paragraph>

        <CodeBlock
          language="json"
          headers={['HTTP/1.1 400 Bad Request', 'Content-Type: application/json']}
        >
          {{
            invalid_parameters: [
              {
                field: 'upload.approach',
                error_code: 2230,
                developer_message:
                  'An invalid upload approach was provided. The only valid approaches for versions greater than 3.0 are `pull`, `post`, and `tus`.',
              },
            ],
            developer_message:
              "The parameters passed to this API endpoint didn't pass Vimeo's validation. Please check the `invalid_parameters` list for more information.",
            error_code: 2204,
          }}
        </CodeBlock>

        <Paragraph size="2">
          See{' '}
          <Link variant="minimal" href="#table-7">
            Table 7
          </Link>{' '}
          for the most frequent cases.
        </Paragraph>

        <GuideTable
          tableNumber={7}
          description="Error codes for bad parameters"
          headers={['Error code', 'Description']}
          rows={[
            [
              '2002',
              <span>
                You haven't specified the{' '}
                <code>
                  <b>size</b>
                </code>{' '}
                parameter when{' '}
                <code>
                  <b>upload.approach</b>
                </code>{' '}
                is <code>tus</code>.
              </span>,
            ],
            [
              '2230',
              <span>
                You have an invalid value for the{' '}
                <code>
                  <b>upload.approach</b>
                </code>{' '}
                field. Use <code>tus</code>, <code>post</code>, or <code>pull</code>.
              </span>,
            ],
            [
              '3116',
              <span>
                you're attempting to use the old{' '}
                <code>
                  <b>type</b>
                </code>{' '}
                field for specifying the upload approach instead of{' '}
                <code>
                  <b>upload.approach</b>
                </code>
                .
              </span>,
            ],
          ]}
        />

        <Paragraph size="2">
          Be sure to check your parameters for accuracy in both the header and the body of the
          request. The bad parameters could be in either place.
        </Paragraph>

        <Header size="3" id="error-code-2205" data-no-sidebar>
          Error code 2205: bad body format
        </Header>
        <Paragraph size="2">
          With a 2205 error and HTTP status 400 Bad Request, check the formatting of the request
          body. The API doesn't like the way that you currently have it.
        </Paragraph>

        <CodeBlock
          language="json"
          headers={['HTTP/1.1 400 Bad Request', 'Content-Type: application/json']}
        >
          {{
            developer_message:
              "The body of this HTTP request isn't formatted properly. Please check the `Content-Type` header and raw body.",
            error_code: 2205,
          }}
        </CodeBlock>

        <Paragraph size="2">
          You might need to change the value of the{' '}
          <code>
            <b>Content-Type</b>
          </code>{' '}
          header if there's a mismatch.
        </Paragraph>

        <Header size="3" id="error-code-2510" data-no-sidebar>
          Error code 2510: invalid URL
        </Header>
        <Paragraph size="2">
          If you're attempting a pull upload and the API responds with the 2510 error, check the
          syntax of the URL. You must URL-encode any unsafe characters and avoid reserved
          characters, and you shouldn't escape slashes. For complete details on URL syntax, see{' '}
          <Link variant="minimal" href="https://www.ietf.org/rfc/rfc1738.txt">
            RFC1738: Uniform Resource Locators (URLs)
          </Link>
          .
        </Paragraph>

        <Header size="3" id="error-code-4101-and-4102-and-4104" data-no-sidebar>
          Error codes 4101, 4102, and 4104: not enough space
        </Header>
        <Paragraph size="2">
          The 4101, 4102, and 4104 errors with HTTP status 403 Forbidden mean that your end user's
          Vimeo account has reached an upload limit:
        </Paragraph>

        <ul>
          <li>
            <strong>If you get error code 4101,</strong> you've hit the account's yearly (or
            lifetime) total.
          </li>
          <li>
            <strong>If you get error code 4102,</strong> you've hit the account's weekly quota.
          </li>
          <li>
            <strong>If you get error code 4104,</strong> you've hit the account's daily quota.
          </li>
        </ul>

        <Paragraph size="2">The API response looks something like this:</Paragraph>

        <CodeBlock
          language="json"
          headers={['HTTP/1.1 403 Forbidden', 'Content-Type: application/json']}
        >
          {{
            developer_message: 'Basic users have limited disk space for a given period.',
            error_code: 4101,
          }}
        </CodeBlock>

        <Paragraph size="2">
          The answer to any such error is more upload space (see{' '}
          <Link variant="minimal" href="#table-8">
            Table 8
          </Link>
          ). We encourage you to direct your maxed-out end users to{' '}
          <Link variant="minimal" href="https://vimeo.com/upgrade">
            vimeo.com/upgrade
          </Link>{' '}
          so that they can upgrade their membership.
        </Paragraph>

        <GuideTable
          tableNumber={8}
          description="Upload quota by membership level"
          headers={['Membership', 'Quota']}
          rows={[
            [
              'Vimeo Free',
              '500 MB per week, up to 5 GB total, with a limit of 10 uploads per day.',
            ],
            ['Vimeo Starter', '5 GB per week, up to 250 GB per year.'],
            ['Vimeo Standard', '20 GB per week, up to 1 TB per year.'],
            ['Vimeo Advanced', 'No weekly limit, up to 7 TB total.'],
          ]}
        />

        <Header size="3" id="error-code-8002" data-no-sidebar>
          Error code 8002: unrecognized access token
        </Header>
        <Paragraph size="2">
          When error code 8002 comes back with HTTP status 401 Authorization Required, we don't
          recognize the access token.
        </Paragraph>

        <CodeBlock
          language="json"
          headers={['HTTP/1.1 401 Authorization Required', 'Content-Type: application/json']}
        >
          {{
            error: 'You must provide a valid authenticated access token.',
          }}
        </CodeBlock>

        <Paragraph size="2">
          Double-check the token string and try again. If you need additional help, see our{' '}
          <Link variant="minimal" href="/api/authentication">
            authentication documentation
          </Link>
          .
        </Paragraph>

        <Header size="2" id="whats-next">
          What's next?
        </Header>
        <Paragraph size="2">
          Now that you've gotten a feel for upload workflows with our API, it's time to deepen and
          broaden your knowledge. For example, you can learn how to add{' '}
          <Link variant="minimal" href="/api/upload/texttracks">
            text tracks
          </Link>{' '}
          and{' '}
          <Link variant="minimal" href="/api/upload/pictures">
            thumbnails
          </Link>
          . You can also find complete information about our entire API in our{' '}
          <Link variant="minimal" href="/api/reference">
            API Reference
          </Link>
          .
        </Paragraph>

        <Paragraph size="2">
          And if anything has left you confused or questioning, real live humans are at your
          disposal. You have only to{' '}
          <Link variant="minimal" href="http://www.vimeo.com/help/contact">
            contact us
          </Link>
          .
        </Paragraph>
      </GuideWrapper>
    </>
  );
}
